<template>
  <div>
    <Header />

    <!-- ── Page Header ── -->
    <div class="page-header">
      <div>
        <h5 class="page-title">Pay Grades</h5>
        <p class="page-sub">Create and manage employee pay grades and salary structures</p>
      </div>
      <div class="page-actions">

        <div class="search-wrap">
          <font-awesome-icon icon="search" class="search-icon" />
          <input type="text" class="search-input" placeholder="Search pay grades..."
            v-model="tableData.search" @input="tableData.page = 1; fetchData()" />
        </div>

        <select v-model="tableData.status" @change="tableData.page = 1; fetchData()" class="filter-select">
            <option :value="[0,1]">All Statuses</option>
            <option :value="[1]">Active</option>
            <option :value="[0]">Inactive</option>
          </select>
  
          <div class="dropdown">
            <button class="btn-outline dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
              <font-awesome-icon icon="download" /> Export
            </button>
            <ul class="dropdown-menu dropdown-menu-end">
              <li><a class="dropdown-item" href="#" @click="drawPdf(print_content, headerArrayColumn, 'p', 'payroll_statutory_deduction', 'Payroll Statutory Deduction')">
                <font-awesome-icon icon="file-pdf" class="me-2 text-danger" /> PDF
              </a></li>
              <li><a class="dropdown-item" href="#" @click="reusableMixinToCSV('payroll_statutory_deduction.csv')">
                <font-awesome-icon icon="file-csv" class="me-2 text-success" /> CSV
              </a></li>
            </ul>
          </div>

        <button v-if="permission?.manage_paygrade" class="btn-primary-action" @click="openCreateModal">
          <font-awesome-icon icon="plus" /> New Pay Grade
        </button>
      </div>
    </div>

    <!-- ── Stat Cards ── -->
    <div class="stat-cards">
      <div class="stat-card">
        <div class="stat-icon" style="background:rgba(0,74,173,.1);color:#004AAD;">
          <font-awesome-icon icon="layer-group" />
        </div>
        <div>
          <div class="stat-value">{{ details.length }}</div>
          <div class="stat-label">Total Pay Grades</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon" style="background:rgba(22,163,74,.1);color:#16a34a;">
          <font-awesome-icon icon="users" />
        </div>
        <div>
          <div class="stat-value">{{ totalEmployees }}</div>
          <div class="stat-label">Total Employees</div>
        </div>
      </div>
    </div>

    <!-- ── Data Card ── -->
    <div class="data-card">

      <!-- Loading -->
      <div v-if="loading" class="state-block">
        <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
        <p class="mt-3 text-muted-cell">Loading pay grades...</p>
      </div>

      <!-- Access denied -->
      <div v-else-if="permissionStatus === 403" class="state-block">
        <div class="state-icon state-icon--red"><font-awesome-icon icon="lock" /></div>
        <p class="state-title">Access Denied</p>
        <p class="state-sub">You don't have permission to view pay grades.</p>
      </div>

      <!-- Empty -->
      <div v-else-if="details.length === 0" class="state-block">
        <div class="state-icon state-icon--blue"><font-awesome-icon icon="layer-group" /></div>
        <p class="state-title">No pay grades found</p>
        <p class="state-sub">Create your first pay grade to get started.</p>
        <button v-if="permission?.manage_paygrade" class="btn-primary-action mt-3" @click="openCreateModal">
          <font-awesome-icon icon="plus" /> New Pay Grade
        </button>
      </div>

      <template v-else>
        <!-- Table toolbar -->
        <div class="table-toolbar">
          <span class="table-count">
            Showing <strong>{{ pagination.from }}–{{ pagination.to }}</strong>
            of <strong>{{ pagination.total }}</strong>
          </span>
          <div class="toolbar-right">
            <label class="table-count">Rows:</label>
            <select v-model="tableData.length" @change="tableData.page = 1; fetchData()" class="rows-select">
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option :value="pagination.total">All</option>
            </select>
          </div>
        </div>

        <!-- Table -->
        <div class="table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Grade Name</th>
                <th>Description</th>
                <th class="text-right">Basic Salary</th>
                <th class="text-right">Min Salary</th>
                <th class="text-right">Max Salary</th>
                <th>Currency</th>
                <th>Employees</th>
                <th>Status</th>
                <th class="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in details" :key="row.id">
                <td class="text-muted-cell">{{ index + 1 }}</td>
                <td>
                  <router-link class="grade-name-link" :to="$url('/paygrade/' + row.id)">
                    {{ row.name }}
                  </router-link>
                </td>
                <td class="text-muted-cell">{{ row.description || '—' }}</td>
                <td class="text-right fw-bold">{{ formatPrice(row.basic_salary) }}</td>
                <td class="text-right amount-muted">{{ formatPrice(row.min_salary) }}</td>
                <td class="text-right amount-muted">{{ formatPrice(row.max_salary) }}</td>
                <td><span class="currency-badge">{{ row.currency || 'NGN' }}</span></td>
                <td>
                  <span class="employee-count">
                    <font-awesome-icon icon="users" class="me-1" />
                    {{ row.employeeCount }}
                  </span>
                </td>
                <td>
                    <label class="toggle-switch">
                      <input
                        type="checkbox"
                        :true-value="1"
                        :false-value="0"
                        v-model="statuses[index]"
                        @change="updateStatus(row.id)"
                      />
                      <span class="toggle-track"><span class="toggle-thumb"></span></span>
                      <span class="toggle-label" :class="statuses[index] ? 'on' : 'off'">
                        {{ statuses[index] ? 'Active' : 'Inactive' }}
                      </span>
                    </label>
                  </td>
                <td class="col-actions">
                  <div class="dropdown">
                    <button class="action-dropdown-btn" type="button"
                      data-bs-toggle="dropdown" data-bs-strategy="fixed" aria-expanded="false">
                      <font-awesome-icon icon="ellipsis-v" />
                    </button>
                    <ul class="dropdown-menu dropdown-menu-end action-menu">
                      <li>
                        <router-link class="action-menu-item" :to="$url('/paygrade/' + row.id)">
                          <font-awesome-icon icon="eye" class="action-menu-icon action-menu-icon--view" />
                          View Details
                        </router-link>
                      </li>
                      <li v-if="permission?.manage_paygrade">
                        <button class="action-menu-item" @click="openUpdateModal(index)">
                          <font-awesome-icon icon="pen" class="action-menu-icon action-menu-icon--edit" />
                          Edit Grade
                        </button>
                      </li>
                      <li v-if="permission?.manage_paygrade">
                        <hr class="dropdown-divider" />
                        <button class="action-menu-item action-menu-item--danger"
                          @click="getOneModal(index)" data-bs-toggle="modal" data-bs-target="#deleteModal">
                          <font-awesome-icon icon="trash" class="action-menu-icon" />
                          Delete
                        </button>
                      </li>
                    </ul>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="table-footer">
          <pagination :pagination="pagination"
            @prev="tableData.page = pagination.currentPage - 1; fetchData()"
            @next="tableData.page = pagination.currentPage + 1; fetchData()" />
        </div>
      </template>
    </div>


    <!-- ══════════════════════════════════════════════
         CREATE MODAL
    ══════════════════════════════════════════════ -->
    <div class="modal fade" id="createModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
        <div class="modal-content modal-clean">

          <div class="modal-header-clean">
            <div class="modal-title-icon" style="background:rgba(0,74,173,.1);color:#004AAD;">
              <font-awesome-icon icon="layer-group" />
            </div>
            <div>
              <h6 class="modal-clean-title">New Pay Grade</h6>
              <p class="modal-clean-sub">Define a salary structure for this grade</p>
            </div>
            <button type="button" class="btn-close ms-auto" data-bs-dismiss="modal"></button>
          </div>

          <form @submit.prevent="createPaygrade">
            <div class="modal-body">
              <div class="modal-fields">

                <!-- Row 1: Name + Currency -->
                <div class="fields-grid">
                  <div class="field-group">
                    <label class="field-label">Grade Name <span class="req">*</span></label>
                    <input type="text" class="field-input" placeholder="e.g. Senior Level"
                      v-model="form.name" required />
                  </div>
                  <div class="field-group">
                    <label class="field-label">Currency <span class="req">*</span></label>
                    <select class="field-input field-select" v-model="form.currency" required>
                      <option value="NGN">NGN — Nigerian Naira</option>
                      <option value="GBP">GBP — British Pound</option>
                      <option value="USD">USD — US Dollar</option>
                    </select>
                  </div>
                </div>

                <!-- Row 2: Basic + Tax Rate -->
                <div class="fields-grid">
                  <div class="field-group">
                    <label class="field-label">Basic Salary <span class="req">*</span></label>
                    <input type="number" class="field-input" placeholder="e.g. 250000"
                      v-model="form.basic_salary" min="0" @keydown="blockInvalidKeys" required />
                  </div>
                  <div class="field-group">
                    <label class="field-label">Monthly Tax Rate (%) <span class="req">*</span></label>
                    <input type="number" class="field-input" placeholder="e.g. 1.5"
                      v-model="form.tax_rate" min="0" @keydown="blockInvalidKeys" required />
                  </div>
                </div>

                <!-- Row 3: Min + Max salary -->
                <div class="fields-grid">
                  <div class="field-group">
                    <label class="field-label">Minimum Salary <span class="req">*</span></label>
                    <input type="number" class="field-input" placeholder="e.g. 200000"
                      v-model="form.min_salary" min="0" @keydown="blockInvalidKeys" required />
                  </div>
                  <div class="field-group">
                    <label class="field-label">Maximum Salary <span class="req">*</span></label>
                    <input type="number" class="field-input" placeholder="e.g. 350000"
                      v-model="form.max_salary" min="0" @keydown="blockInvalidKeys" required />
                  </div>
                </div>

                <!-- Description -->
                <div class="field-group">
                  <label class="field-label">Description</label>
                  <textarea class="field-input" rows="2"
                    placeholder="Brief description of this pay grade"
                    v-model="form.description"></textarea>
                </div>

                <!-- Salary Components -->
                <div class="section-divider"><span>Salary Components</span></div>

                <div v-if="loadingComponents" class="state-block" style="padding:24px 0;">
                  <div class="spinner-border text-primary" style="width:2rem;height:2rem;" role="status"></div>
                  <p class="mt-3 text-muted-cell" style="font-size:13px;">Loading components...</p>
                </div>

                <div v-else-if="availableComponents.length === 0" class="state-block" style="padding:20px 0;">
                  <p class="text-muted-cell" style="font-size:13px;">No salary components available. Create components first.</p>
                </div>

                <div v-else class="components-list">
                  <div v-for="comp in availableComponents" :key="comp.id"
                    class="component-row"
                    :class="{ 'component-row--selected': selectedComponents.includes(comp.id) }">

                    <!-- Top: checkbox + name + badges -->
                    <div class="component-top">
                      <input type="checkbox" class="component-checkbox"
                        :checked="selectedComponents.includes(comp.id)"
                        @change="toggleComponent(comp.id)" />
                      <div class="component-info">
                        <p class="component-name">{{ comp.name }}</p>
                        <div class="component-badges">
                          <span class="comp-badge"
                            :class="comp.type === 'earning' ? 'comp-badge--earning' : 'comp-badge--deduction'">
                            {{ comp.type }}
                          </span>
                          <span class="comp-badge comp-badge--neutral">{{ comp.calculation_type }}</span>
                        </div>
                      </div>
                    </div>

                    <!-- Expanded: custom value input full-width below -->
                    <div v-if="selectedComponents.includes(comp.id)" class="component-value-wrap">
                      <label class="comp-value-label">Custom Value</label>
                      <input type="number" class="field-input comp-value-input"
                        v-model="componentValues[comp.id]"
                        min="0" placeholder="0"
                        @keydown="blockInvalidKeys" />
                    </div>

                  </div>
                </div>

              </div>
            </div>

            <div class="modal-footer border-0 pt-0">
              <button type="button" class="btn-outline" data-bs-dismiss="modal">Cancel</button>
              <button type="submit" class="btn-primary-action" :disabled="crudLoading">
                <span v-if="crudLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                {{ crudLoading ? 'Creating...' : 'Create Pay Grade' }}
              </button>
            </div>
          </form>

        </div>
      </div>
    </div>


    <!-- ══════════════════════════════════════════════
         EDIT MODAL
    ══════════════════════════════════════════════ -->
    <div class="modal fade" id="editModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
        <div class="modal-content modal-clean">

          <div class="modal-header-clean">
            <div class="modal-title-icon" style="background:rgba(0,74,173,.1);color:#004AAD;">
              <font-awesome-icon icon="pen" />
            </div>
            <div>
              <h6 class="modal-clean-title">Edit Pay Grade</h6>
              <p class="modal-clean-sub">{{ currentData.reference }}</p>
            </div>
            <button type="button" class="btn-close ms-auto" data-bs-dismiss="modal"></button>
          </div>

          <form @submit.prevent="updatePaygrade">
            <div class="modal-body">
              <div class="modal-fields">

                <div class="fields-grid">
                  <div class="field-group">
                    <label class="field-label">Grade Name <span class="req">*</span></label>
                    <input type="text" class="field-input" v-model="editForm.name" required />
                  </div>
                  <div class="field-group">
                    <label class="field-label">Currency <span class="req">*</span></label>
                    <select class="field-input field-select" v-model="editForm.currency" required>
                      <option value="NGN">NGN — Nigerian Naira</option>
                      <option value="GBP">GBP — British Pound</option>
                      <option value="USD">USD — US Dollar</option>
                    </select>
                  </div>
                </div>

                <div class="fields-grid">
                  <div class="field-group">
                    <label class="field-label">Basic Salary <span class="req">*</span></label>
                    <input type="number" class="field-input" v-model="editForm.basic_salary"
                      min="0" @keydown="blockInvalidKeys" required />
                  </div>
                  <div class="field-group">
                    <label class="field-label">Monthly Tax Rate (%)</label>
                    <input type="number" class="field-input" v-model="editForm.tax_rate"
                      min="0" @keydown="blockInvalidKeys" />
                  </div>
                </div>

                <div class="fields-grid">
                  <div class="field-group">
                    <label class="field-label">Minimum Salary</label>
                    <input type="number" class="field-input" v-model="editForm.min_salary"
                      min="0" @keydown="blockInvalidKeys" />
                  </div>
                  <div class="field-group">
                    <label class="field-label">Maximum Salary</label>
                    <input type="number" class="field-input" v-model="editForm.max_salary"
                      min="0" @keydown="blockInvalidKeys" />
                  </div>
                </div>

                <div class="field-group">
                  <label class="field-label">Description</label>
                  <textarea class="field-input" rows="2" v-model="editForm.description"></textarea>
                </div>

              </div>
            </div>

            <div class="modal-footer border-0 pt-0">
              <button type="button" class="btn-outline" data-bs-dismiss="modal">Cancel</button>
              <button type="submit" class="btn-primary-action" :disabled="crudLoading">
                <span v-if="crudLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                {{ crudLoading ? 'Saving...' : 'Save Changes' }}
              </button>
            </div>
          </form>

        </div>
      </div>
    </div>


    <!-- ══════════════════════════════════════════════
         DELETE CONFIRM MODAL
    ══════════════════════════════════════════════ -->
    <div class="modal fade" id="deleteModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-sm">
        <div class="modal-content modal-clean">
          <div class="modal-icon-wrap">
            <div class="modal-icon modal-icon--red">
              <font-awesome-icon icon="trash" />
            </div>
          </div>
          <div class="modal-body-clean">
            <p class="modal-heading">Delete Pay Grade?</p>
            <p class="modal-subtext">
              Are you sure you want to delete
              <strong>{{ currentData.name }}</strong>? This action cannot be undone.
            </p>
          </div>
          <div class="modal-actions">
            <button type="button" class="modal-btn modal-btn--cancel" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="modal-btn modal-btn--danger" :disabled="crudLoading"
              @click="deletePaygrade">
              <span v-if="crudLoading" class="spinner-border spinner-border-sm me-1" role="status"></span>
              <font-awesome-icon v-else icon="trash" class="me-1" />
              {{ crudLoading ? 'Deleting...' : 'Yes, Delete' }}
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>


<script>
import Header from './component/header.vue';
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { useStore } from 'vuex';
import { reusableMixin } from '../../../../lib/reusableMixin.js';

export default {
  name: 'PaygradesPage',

  components: { Header },

  mixins: [reusableMixin],

  data() {
    return {
      details:          [],
      statuses: [],
      availableComponents: [],
      loading:          false,
      loadingComponents: false,
      crudLoading:      false,
      permissionStatus: null,
      currentData:      {},

      // Create form
      form: {
        name:         '',
        description:  '',
        basic_salary: '',
        min_salary:   '',
        max_salary:   '',
        tax_rate:     '',
        currency:     'NGN',
      },

      // Edit form
      editForm: {
        id:           '',
        name:         '',
        description:  '',
        basic_salary: '',
        min_salary:   '',
        max_salary:   '',
        tax_rate:     '',
        currency:     'NGN',
      },

      // Salary component selection (create only)
      selectedComponents: [],
      componentValues:    {},

      tableData: { page: 1,
        draw:   0,
        length: 10,
        search: '',
        column: 0,
        dir:    'desc',
        status: [0, 1],
      },

      pagination: {
        lastPage:    '',
        currentPage: '',
        total:       '',
        lastPageUrl: '',
        nextPageUrl: '',
        prevPageUrl: '',
        from:        '',
        to:          '',
      },

      toast: useToast(),
      store: useStore(),
    };
  },

  computed: {
    permission() {
      return this.store.state.getCurrentUserPermission;
    },

    totalEmployees() {
      return this.details.reduce((sum, pg) => sum + (pg.employeeCount || 0), 0);
    },
  },

  created() {
    this.fetchData();
    this.fetchSalaryComponents();
  },

  methods: {
    // ── Data fetching ──────────────────────────────────────────────────────
    fetchData(url = '/payroll/paygrade') {
      this.loading = true;
      this.tableData.draw++;
      axios.get(url, { params: this.tableData })
        .then(({ data }) => {
          const payload = data?.data?.data ?? data?.data ?? [];
          this.details = (Array.isArray(payload) ? payload : []).map(this.mapPaygrade);
          this.statuses = payload.map(b => b.is_active);
          if (data?.data?.total !== undefined) this.configPagination(data.data);
          else this.setFlatPagination();
        })
        .catch(error => {
          if (error?.response?.status === 403) {
            this.permissionStatus = 403;
            this.toast.error(error.response.data?.message ?? 'Access denied');
          } else {
            this.toast.error('Could not load pay grades. Please try again.');
          }
        })
        .finally(() => { this.loading = false; });
    },

    fetchSalaryComponents() {
      this.loadingComponents = true;
      axios.get('/payroll/salary-component/active')
        .then(({ data }) => {
          const items = data?.data?.data ?? data?.data ?? [];
          this.availableComponents = items.map(this.mapComponent);
        })
        .catch(() => { /* intentionally silent */ })
        .finally(() => { this.loadingComponents = false; });
    },

    // ── Data mappers ──────────────────────────────────────────────────────
    mapPaygrade(item) {
      return {
        id:            item?.id,
        reference:     item?.reference     || '',
        name:          item?.grade_name    || item?.name        || '',
        description:   item?.description   || '',
        currency:      item?.currency      || 'NGN',
        basic_salary:  item?.basic_salary  || 0,
        min_salary:    item?.min_salary    || 0,
        max_salary:    item?.max_salary    || 0,
        tax_rate:      item?.tax_rate      || 0,
        employeeCount: item?.employee_count || item?.employees_count || 0,
      };
    },

    mapComponent(item) {
      return {
        id:               item?.id,
        name:             item?.component_name || item?.name || '',
        type:             item?.type             || 'earning',
        calculation_type: item?.calculation_type || 'fixed',
        value:            Number(item?.custom_value || item?.value || 0),
      };
    },

    // ── Modal helpers ──────────────────────────────────────────────────────
    openCreateModal() {
      // Reset form
      Object.assign(this.form, {
        name: '', description: '', basic_salary: '', min_salary: '',
        max_salary: '', tax_rate: '', currency: 'NGN',
      });
      this.selectedComponents = [];
      this.componentValues    = {};
      window.$('#createModal').modal('show');
    },

    openUpdateModal(index) {
      this.currentData = this.details[index];
      Object.assign(this.editForm, {
        id:           this.currentData.id,
        name:         this.currentData.name,
        description:  this.currentData.description,
        basic_salary: this.currentData.basic_salary,
        min_salary:   this.currentData.min_salary,
        max_salary:   this.currentData.max_salary,
        tax_rate:     this.currentData.tax_rate,
        currency:     this.currentData.currency,
      });
      window.$('#editModal').modal('show');
    },

    getOneModal(index) {
      this.currentData = this.details[index];
    },

    // ── Component selection ────────────────────────────────────────────────
    toggleComponent(id) {
      if (this.selectedComponents.includes(id)) {
        this.selectedComponents = this.selectedComponents.filter(c => c !== id);
        const vals = { ...this.componentValues };
        delete vals[id];
        this.componentValues = vals;
      } else {
        this.selectedComponents = [...this.selectedComponents, id];
        const comp = this.availableComponents.find(c => c.id === id);
        this.componentValues = { ...this.componentValues, [id]: comp?.value ?? 0 };
      }
    },

    // ── CRUD ───────────────────────────────────────────────────────────────
    async createPaygrade() {
      this.crudLoading = true;

      const salary_components = this.selectedComponents.map(id => {
        const comp = this.availableComponents.find(c => c.id === id);
        return {
          salary_component_id: id,
          custom_value:        Number(this.componentValues[id] || 0),
          calculation_type:    comp?.calculation_type,
          type:                comp?.type,
        };
      });

      const payload = { ...this.form, salary_components };

      axios.post('/payroll/paygrade', payload)
        .then(() => {
          this.toast.success('Pay grade created successfully');
          this.fetchData();
          window.$('#createModal').modal('hide');
        })
        .catch(error => {
          this.toast.error(error.response?.data?.message ?? 'Could not create pay grade');
        })
        .finally(() => { this.crudLoading = false; });
    },

    async updatePaygrade() {
      this.crudLoading = true;
      axios.put(`/payroll/paygrade/${this.editForm.id}`, this.editForm)
        .then(() => {
          this.toast.success('Pay grade updated successfully');
          this.fetchData();
          window.$('#editModal').modal('hide');
        })
        .catch(error => {
          this.toast.error(error.response?.data?.message ?? 'Could not update pay grade');
        })
        .finally(() => { this.crudLoading = false; });
    },

    async deletePaygrade() {
      this.crudLoading = true;
      axios.delete(`/payroll/paygrade/${this.currentData.id}`)
        .then(() => {
          this.toast.success('Pay grade deleted successfully');
          this.fetchData();
          window.$('#deleteModal').modal('hide');
        })
        .catch(error => {
          this.toast.error(error.response?.data?.message ?? 'Could not delete pay grade');
        })
        .finally(() => { this.crudLoading = false; });
    },

    async updateStatus(id) {
      try {
        const { data } = await axios.put('payroll/paygrade/update-status/' + id);
        this.toast.success(data.message);
        this.fetchData();
      } catch (err) {
        const status = err.response?.status;
        if (status === 422) this.toast.warning('Invalid request.');
        if (status === 403) this.toast.warning(err.response?.data?.message);
        else this.toast.error('Could not update status.');
      }
    },

    // ── Helpers ────────────────────────────────────────────────────────────
    blockInvalidKeys(e) {
      if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
    },

    formatPrice(value) {
      if (!value && value !== 0) return '0.00';
      return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    configPagination(data) {
      this.pagination.lastPage    = data.last_page;
      this.pagination.currentPage = data.current_page;
      this.pagination.total       = data.total;
      this.pagination.lastPageUrl = data.last_page_url;
      this.pagination.nextPageUrl = data.next_page_url;
      this.pagination.prevPageUrl = data.prev_page_url;
      this.pagination.from        = data.from;
      this.pagination.to          = data.to;
    },

    // Fallback when API returns a flat array without pagination envelope
    setFlatPagination() {
      const n = this.details.length;
      this.pagination.from  = n ? 1 : 0;
      this.pagination.to    = n;
      this.pagination.total = n;
    },
  },
};
</script>


<style scoped>
/* ── Page header ─────────────────────────────────────────────────────── */
.page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
.page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub     { font-size:13px; color:#7a90b0; margin:0; }
.page-actions { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }

/* ── Search ──────────────────────────────────────────────────────────── */
.search-wrap  { position:relative; display:flex; align-items:center; }
.search-icon  { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
.search-input { padding:8px 12px 8px 32px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; width:210px; outline:none; transition:border-color .15s; }
.search-input:focus { border-color:#004AAD; }

/* ── Buttons ─────────────────────────────────────────────────────────── */
.btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; white-space:nowrap; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.5; cursor:not-allowed; }
.btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; transition:background .15s; }
.btn-outline:hover { background:#f4f7fb; }
.me-1 { margin-right:4px; } .me-2 { margin-right:8px; } .mt-3 { margin-top:12px; } .ms-auto { margin-left:auto; }

/* ── Stat cards ──────────────────────────────────────────────────────── */
.stat-cards { display:grid; grid-template-columns:repeat(2,1fr); gap:16px; margin-bottom:20px; }
.stat-card  { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:18px 20px; display:flex; align-items:center; gap:16px; }
.stat-icon  { width:48px; height:48px; border-radius:12px; display:flex; align-items:center; justify-content:center; font-size:20px; flex-shrink:0; }
.stat-value { font-size:26px; font-weight:800; color:#1a2b45; line-height:1; margin-bottom:4px; }
.stat-label { font-size:13px; color:#7a90b0; }

/* ── Data card ───────────────────────────────────────────────────────── */
.data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }

/* ── States ──────────────────────────────────────────────────────────── */
.state-block { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
.state-icon  { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
.state-icon--blue { background:rgba(0,74,173,.1); color:#004AAD; }
.state-icon--red  { background:rgba(224,53,53,.1); color:#e03535; }
.state-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
.state-sub   { font-size:13px; color:#7a90b0; margin:0; }
.text-muted-cell { color:#4a5e78; }

/* ── Table toolbar ───────────────────────────────────────────────────── */
.table-toolbar { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
.table-count   { font-size:13px; color:#7a90b0; }
.toolbar-right { display:flex; align-items:center; gap:8px; }
.rows-select   { border:1px solid #dde5f0; border-radius:7px; padding:5px 10px; font-size:13px; color:#1a2b45; background:#f8fafc; outline:none; cursor:pointer; }

/* ── Table ───────────────────────────────────────────────────────────── */
.table-wrap  { overflow-x:auto; }
.data-table  { width:100%; border-collapse:collapse; }
.data-table thead tr { background:#f8fafc; }
.data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 14px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
.data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
.data-table tbody tr:last-child { border-bottom:none; }
.data-table tbody tr:hover { background:#f8fafc; }
.data-table tbody td { padding:11px 14px; font-size:13px; vertical-align:middle; }
.col-actions { width:48px; text-align:center; }
.text-right  { text-align:right; }
.fw-bold     { font-weight:700; color:#1a2b45; }
.amount-muted { color:#4a5e78; font-weight:500; }

.batch-code { font-family:monospace; font-size:12px; font-weight:600; color:#1a2b45; background:#f4f7fb; border:1px solid #e4eaf2; border-radius:6px; padding:2px 7px; }
.grade-name-link { font-weight:600; color:#004AAD; text-decoration:none; }
.grade-name-link:hover { text-decoration:underline; }
.currency-badge { background:#f0f4f8; color:#4a5e78; font-size:11px; font-weight:700; border-radius:6px; padding:2px 8px; font-family:monospace; }
.employee-count { display:inline-flex; align-items:center; gap:5px; background:#edf4ff; color:#004AAD; font-size:12px; font-weight:600; border-radius:20px; padding:3px 10px; }

/* ── Action dropdown ─────────────────────────────────────────────────── */
.action-dropdown-btn { width:30px; height:30px; border-radius:8px; border:1px solid #dde5f0; background:#fff; display:flex; align-items:center; justify-content:center; font-size:13px; color:#7a90b0; cursor:pointer; transition:background .15s; }
.action-dropdown-btn:hover { background:#f4f7fb; color:#1a2b45; }
.action-menu { min-width:170px; padding:6px; border:1px solid #e4eaf2; border-radius:10px; box-shadow:0 8px 24px rgba(0,0,0,.1); }
.action-menu-item { display:flex; align-items:center; gap:10px; width:100%; padding:8px 12px; font-size:13px; color:#1a2b45; background:transparent; border:none; border-radius:7px; cursor:pointer; text-decoration:none; transition:background .12s; }
.action-menu-item:hover { background:#f4f7fb; text-decoration:none; }
.action-menu-item--danger { color:#e03535; }
.action-menu-item--danger:hover { background:#fff0f0 !important; }
.action-menu-icon       { font-size:12px; width:16px; flex-shrink:0; }
.action-menu-icon--view { color:#0d9488; }
.action-menu-icon--edit { color:#004AAD; }
.dropdown-divider { border-color:#f0f4f8; margin:4px 0; }
.dropdown-menu { border:1px solid #e4eaf2; border-radius:9px; box-shadow:0 8px 24px rgba(0,0,0,.08); padding:6px; min-width:170px; }
.table-footer { padding:14px 20px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; }

/* ── Modals ──────────────────────────────────────────────────────────── */
.modal-clean { border:none; border-radius:16px; box-shadow:0 20px 60px rgba(0,0,0,.14); overflow:hidden; display:flex; flex-direction:column; max-height:92vh; }
.modal-clean form { display:flex; flex-direction:column; flex:1; overflow:hidden; min-height:0; }
.modal-clean .modal-body { overflow-y:auto; flex:1; }
.modal-dialog-scrollable .modal-content { max-height:92vh; }
.modal-header-clean { display:flex; align-items:flex-start; gap:14px; padding:20px 24px 16px; border-bottom:1px solid #f0f4f8; }
.modal-title-icon { width:40px; height:40px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; }
.modal-clean-title { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
.modal-clean-sub   { font-size:12px; color:#7a90b0; margin:0; }

.modal-fields { display:flex; flex-direction:column; gap:14px; }
.fields-grid  { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
.field-group  { display:flex; flex-direction:column; gap:5px; }
.field-label  { font-size:13px; font-weight:600; color:#1a2b45; }
.req          { color:#e03535; }
.field-input  { padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13.5px; color:#1a2b45; background:#fff; outline:none; width:100%; transition:border-color .15s; font-family:inherit; }
.field-input:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
.field-select { appearance:auto; cursor:pointer; }
textarea.field-input { resize:vertical; }

/* ── Section divider ─────────────────────────────────────────────────── */
.section-divider { display:flex; align-items:center; gap:10px; margin:4px 0 2px; }
.section-divider::before, .section-divider::after { content:''; flex:1; height:1px; background:#e4eaf2; }
.section-divider span { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.08em; color:#7a90b0; white-space:nowrap; }

/* ── Salary components ───────────────────────────────────────────────── */
.components-list { display:flex; flex-direction:column; gap:8px; max-height:38vh; overflow-y:auto; padding-right:2px; }
.component-row { display:flex; flex-direction:column; gap:10px; padding:12px 14px; border:1px solid #e4eaf2; border-radius:10px; transition:background .12s, border-color .12s; }
.component-row--selected { background:#f4f8ff; border-color:#c5d6f5; }
.component-row:hover:not(.component-row--selected) { background:#f8fafc; }
.component-top { display:flex; align-items:flex-start; gap:10px; }
.component-checkbox { width:16px; height:16px; accent-color:#004AAD; cursor:pointer; margin-top:3px; flex-shrink:0; }
.component-info { flex:1; min-width:0; }
.component-name { font-size:13px; font-weight:600; color:#1a2b45; margin:0 0 6px; }
.component-badges { display:flex; gap:6px; flex-wrap:wrap; }
.comp-badge { font-size:11px; font-weight:600; border-radius:20px; padding:2px 9px; }
.comp-badge--earning   { background:#f0fdf4; color:#16a34a; border:1px solid #bbf7d0; }
.comp-badge--deduction { background:#fff0f0; color:#e03535; border:1px solid #fecaca; }
.comp-badge--neutral   { background:#f4f7fb; color:#7a90b0; border:1px solid #e4eaf2; }
.component-value-wrap  { display:flex; flex-direction:column; gap:4px; padding-left:26px; }
.comp-value-label { font-size:11px; font-weight:600; color:#7a90b0; }
.comp-value-input { max-width:180px; padding:7px 10px !important; font-size:13px !important; }

/* ── Delete modal ────────────────────────────────────────────────────── */
.modal-icon-wrap { display:flex; justify-content:center; padding:28px 24px 0; }
.modal-icon      { width:60px; height:60px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:26px; }
.modal-icon--red { background:rgba(224,53,53,.1); color:#e03535; }
.modal-body-clean { padding:16px 28px 8px; text-align:center; }
.modal-heading  { font-size:17px; font-weight:700; color:#1a2b45; margin:0 0 8px; }
.modal-subtext  { font-size:13.5px; color:#7a90b0; margin:0; line-height:1.6; }
.modal-actions  { display:flex; gap:10px; padding:18px 24px 24px; justify-content:center; }
.modal-btn      { display:inline-flex; align-items:center; justify-content:center; font-size:13px; font-weight:600; padding:9px 22px; border-radius:8px; border:none; cursor:pointer; flex:1; transition:background .15s; font-family:inherit; }
.modal-btn--cancel { background:#f4f7fb; color:#1a2b45; border:1px solid #dde5f0; }
.modal-btn--danger { background:#e03535; color:#fff; }
.modal-btn--danger:hover:not(:disabled) { background:#c02828; }
.modal-btn:disabled { opacity:.6; cursor:not-allowed; }

/* ── Responsive ──────────────────────────────────────────────────────── */
@media (max-width:991px) {
  .page-header, .page-actions { flex-direction:column; align-items:stretch; }
  .search-input { width:100%; }
  .stat-cards   { grid-template-columns:1fr; }
}
@media (max-width:640px) {
  .fields-grid          { grid-template-columns:1fr; }
  .modal-dialog         { margin:8px; }
  .modal-content.modal-clean { border-radius:12px; }
  .comp-value-input     { max-width:100% !important; }
  .component-value-wrap { padding-left:0; }
}

.filter-select { padding:8px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; cursor:pointer; }

/* ── Toggle switch ───────────────────────────────────────────────────── */
.toggle-switch { display:inline-flex; align-items:center; gap:8px; cursor:pointer; user-select:none; }
.toggle-switch input { display:none; }
.toggle-track  { position:relative; width:36px; height:20px; border-radius:20px; background:#d1d9e8; transition:background .2s; flex-shrink:0; }
.toggle-switch input:checked + .toggle-track { background:#16a34a; }
.toggle-thumb  { position:absolute; top:3px; left:3px; width:14px; height:14px; border-radius:50%; background:#fff; box-shadow:0 1px 3px rgba(0,0,0,.2); transition:transform .2s; }
.toggle-switch input:checked + .toggle-track .toggle-thumb { transform:translateX(16px); }
.toggle-label  { font-size:12px; font-weight:600; }
.toggle-label.on  { color:#16a34a; }
.toggle-label.off { color:#7a90b0; }
</style>